export type TranscriptLineType = "action" | "spoken";

export interface TranscriptLine {
  time: string;
  type: TranscriptLineType;
  text: string;
}

export interface TranscriptSection {
  label: string;
  lines: TranscriptLine[];
}

export interface StructuredTranscript {
  sections: TranscriptSection[];
}

const SECTION_LABELS = [
  "HOOK",
  "BODY",
  "PAYOFF",
  "INTRO",
  "OUTRO",
  "CTA",
  "SETUP",
  "CLIMAX",
] as const;

const SECTION_SET = new Set(
  SECTION_LABELS.map((label) => label.toLowerCase())
);

const TYPE_MARKERS = "ACTION|SPOKEN|SPEAKING|SPEAKS|DIALOGUE|SAID";

const ENTRY_REGEX = new RegExp(
  `^(\\d{1,2}:\\d{2}(?::\\d{2})?)\\s+(${TYPE_MARKERS})\\s*(.*)$`,
  "i"
);

const INLINE_TYPE_REGEX = new RegExp(
  `^(${TYPE_MARKERS})\\s*(.*)$`,
  "i"
);

const CINEMATIC_PATTERNS =
  /\b(shot|close-up|closeup|wide|cut to|cut back|frame|camera|zoom|pan|screen|overlay|visual|footage|clip|scene|angle|pov|b-roll|group of|looking|speaking to|on a deck|tropical)\b/i;

function normalizeTranscript(raw: string): string {
  let text = raw.trim();

  for (const label of SECTION_LABELS) {
    text = text.replace(
      new RegExp(`\\b${label}\\b`, "gi"),
      `\n${label}\n`
    );
  }

  text = text.replace(
    new RegExp(`(?<!\\n)(\\d{1,2}:\\d{2}(?::\\d{2})?)\\s+(${TYPE_MARKERS})\\b`, "gi"),
    "\n$1 $2"
  );

  // Split inline type markers that appear mid-block (same timestamp).
  text = text.replace(
    new RegExp(`\\s+(${TYPE_MARKERS})\\s+`, "gi"),
    "\n$1 "
  );

  return text;
}

function stripQuotes(text: string): string {
  const trimmed = text.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function markerToType(marker: string): TranscriptLineType {
  const normalized = marker.toLowerCase();
  if (
    normalized === "spoken" ||
    normalized === "speaking" ||
    normalized === "speaks" ||
    normalized === "dialogue" ||
    normalized === "said"
  ) {
    return "spoken";
  }
  return "action";
}

function looksLikeDialogue(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (CINEMATIC_PATTERNS.test(trimmed)) return false;
  if (/^(Cut to|Wide shot|Close-up|Closeup|Group of|Screen)/i.test(trimmed)) {
    return false;
  }

  if (
    /^(I |I'm |I've |I'll |you |y'all |we |they |it's |that's |don't |can't |won't |oh |yeah |so |thank|if anything|i just feel|i came|i'm glad)/i.test(
      trimmed
    )
  ) {
    return true;
  }

  if (/[?!]/.test(trimmed) && !CINEMATIC_PATTERNS.test(trimmed)) return true;

  if (
    /\b(I'm|you're|we're|gonna|wanna|gotta|like this|my person|y'all|thank you)\b/i.test(
      trimmed
    )
  ) {
    return true;
  }

  return false;
}

function stripTypePrefix(text: string): string {
  return text.replace(new RegExp(`^(${TYPE_MARKERS})\\s*:?\\s*`, "i"), "").trim();
}

function parseTimestampBlock(time: string, rawText: string): TranscriptLine[] {
  const text = stripTypePrefix(rawText.trim().replace(/^[-–—:.\s]+/, ""));
  if (!text) return [];

  const lines: TranscriptLine[] = [];
  // Use double quotes only — single quotes break on contractions like y'all.
  const quoteRegex = /"([^"]+)"/g;
  let lastIndex = 0;
  let foundQuote = false;
  let match: RegExpExecArray | null;

  while ((match = quoteRegex.exec(text)) !== null) {
    foundQuote = true;
    const before = stripTypePrefix(text.slice(lastIndex, match.index).trim());
    if (before) {
      lines.push({ time, type: "action", text: stripQuotes(before) });
    }

    const spoken = match[1].trim();
    if (spoken) {
      lines.push({ time, type: "spoken", text: spoken });
    }

    lastIndex = match.index + match[0].length;
  }

  const remainder = stripTypePrefix(text.slice(lastIndex).trim());
  if (remainder) {
    lines.push({
      time,
      type: looksLikeDialogue(remainder) ? "spoken" : "action",
      text: stripQuotes(remainder),
    });
  }

  if (!foundQuote && lines.length === 0) {
    lines.push({
      time,
      type: looksLikeDialogue(text) ? "spoken" : "action",
      text: stripQuotes(text),
    });
  }

  return lines;
}

function parseEntryLine(line: string): TranscriptLine | null {
  const match = line.match(ENTRY_REGEX);
  if (!match) return null;

  const time = match[1];
  const type = markerToType(match[2]);
  const text = stripQuotes(match[3].trim());
  if (!text) return null;

  return { time, type, text };
}

function splitTimestampSegments(transcript: string): TranscriptLine[] {
  const regex = /(\d{1,2}:\d{2}(?::\d{2})?)/g;
  const matches = [...transcript.matchAll(regex)];
  if (matches.length === 0) return [];

  const lines: TranscriptLine[] = [];
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const time = match[1];
    const start = (match.index ?? 0) + match[0].length;
    const end =
      i + 1 < matches.length
        ? matches[i + 1].index ?? transcript.length
        : transcript.length;
    const block = transcript.slice(start, end);
    lines.push(...parseTimestampBlock(time, block));
  }
  return lines;
}

function groupLinesByTimestamp(lines: TranscriptLine[]): TranscriptLine[][] {
  const groups: TranscriptLine[][] = [];
  for (const line of lines) {
    const last = groups[groups.length - 1];
    if (last && last[0].time === line.time) {
      last.push(line);
    } else {
      groups.push([line]);
    }
  }
  return groups;
}

function assignHeuristicSections(lines: TranscriptLine[]): TranscriptSection[] {
  const groups = groupLinesByTimestamp(lines);
  if (groups.length === 0) return [];

  if (groups.length === 1) {
    return [{ label: "HOOK", lines: groups[0] }];
  }

  if (groups.length === 2) {
    return [
      { label: "HOOK", lines: groups[0] },
      { label: "PAYOFF", lines: groups[1] },
    ];
  }

  const bodyLines = groups.slice(1, -1).flat();
  return [
    { label: "HOOK", lines: groups[0] },
    { label: "BODY", lines: bodyLines },
    { label: "PAYOFF", lines: groups[groups.length - 1] },
  ];
}

/**
 * Parse Sociable play-by-play transcripts into HOOK / BODY / PAYOFF sections
 * with ACTION and SPEAKING lines when possible.
 */
export function parseStructuredTranscript(
  transcript: string
): StructuredTranscript | null {
  const normalized = normalizeTranscript(transcript);
  const rawLines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const sections: TranscriptSection[] = [];
  let currentSection: TranscriptSection | null = null;
  let foundStructuredLine = false;

  // Find-or-create by label; assignment to currentSection happens at the call
  // sites so TypeScript's control-flow analysis can track it.
  const findOrCreateSection = (label: string): TranscriptSection => {
    const existing = sections.find(
      (section) => section.label.toLowerCase() === label.toLowerCase()
    );
    if (existing) return existing;
    const created: TranscriptSection = { label: label.toUpperCase(), lines: [] };
    sections.push(created);
    return created;
  };

  for (const line of rawLines) {
    const sectionLabel = line.replace(/[^a-z]/gi, "").toLowerCase();
    if (SECTION_SET.has(sectionLabel) && line.length <= 12) {
      currentSection = findOrCreateSection(line);
      continue;
    }

    const entry = parseEntryLine(line);
    if (entry) {
      foundStructuredLine = true;
      if (!currentSection) currentSection = findOrCreateSection("SCRIPT");
      currentSection.lines.push(entry);
      continue;
    }

    const inlineTypeMatch = line.match(INLINE_TYPE_REGEX);
    if (inlineTypeMatch && currentSection) {
      const last = currentSection.lines[currentSection.lines.length - 1];
      if (last) {
        foundStructuredLine = true;
        currentSection.lines.push({
          time: last.time,
          type: markerToType(inlineTypeMatch[1]),
          text: stripQuotes(inlineTypeMatch[2].trim()),
        });
      }
      continue;
    }

    // Timestamped block without an explicit type marker.
    const timestampOnly = line.match(/^(\d{1,2}:\d{2}(?::\d{2})?)\s+(.*)$/);
    if (timestampOnly) {
      foundStructuredLine = true;
      if (!currentSection) currentSection = findOrCreateSection("SCRIPT");
      currentSection.lines.push(
        ...parseTimestampBlock(timestampOnly[1], timestampOnly[2])
      );
    }
  }

  if (foundStructuredLine && sections.length > 0) {
    return { sections: sections.filter((section) => section.lines.length > 0) };
  }

  const fallbackLines = splitTimestampSegments(transcript);
  if (fallbackLines.length === 0) return null;

  return {
    sections: assignHeuristicSections(fallbackLines),
  };
}

export function getTranscriptLineLabel(type: TranscriptLineType): string {
  return type === "spoken" ? "SPEAKING" : "ACTION";
}

export function formatStructuredTranscriptForCopy(
  transcript: string
): string {
  const parsed = parseStructuredTranscript(transcript);
  if (!parsed) return transcript;

  return parsed.sections
    .map((section) => {
      const groups = groupLinesByTimestamp(section.lines);
      const body = groups
        .map((group) =>
          group
            .map((line) => {
              const content =
                line.type === "spoken" ? `"${line.text}"` : line.text;
              return `[${line.time}] ${getTranscriptLineLabel(line.type)} ${content}`;
            })
            .join("\n")
        )
        .join("\n");
      return `${section.label}\n${body}`;
    })
    .join("\n\n");
}

export { groupLinesByTimestamp };
